/**
 * The console's landing page — an action-needed inbox (ux.md §6.1), not a stats page.
 *
 * ## What this screen is arguing with
 *
 * Every e-signature console on the market opens the same way: a grid of KPI cards, each with a big
 * number, a percentage delta and a sparkline. DocuSign's Transaction Rooms dashboard is the type
 * specimen — six of them, two donuts, a row of filter dropdowns above the fold, and the sparklines
 * captioned EXAMPLE DATA in the shipping product, because the chart was decoration and nobody could
 * source the series. It tells you how much happened. It does not tell you what to do, and a person
 * signing in at nine in the morning is not asking a reporting question.
 *
 * So this screen is built in the order somebody actually asks:
 *
 *   1. **Is anything on ME?** The masthead answers in a sentence, from `public.my_inbox()` — the
 *      real documents addressed to this person's confirmed email — and the answer carries the
 *      button that acts on it. Not a count with a navigation behind it: the queue below opens the
 *      ceremony from here.
 *   2. **Where is everything I sent?** One proportional bar, not four tiles. The four figures are
 *      one set cut four ways (`bucketEnvelopeCounts`), and a bar shows the shape of the pipeline
 *      before a single number is read. There is no trend line, because this product has never
 *      measured a trend on this screen and drawing one would be the same lie in our own colours.
 *   3. **What is moving?** The last five envelopes, as a table, because those columns are a
 *      comparison and a card grid is the layout that makes comparison hardest.
 *   4. **What is this costing, who am I, what is left to set up?** A rail, narrower and never
 *      first in the DOM on a phone — context must not outrank work.
 *   5. **Start something.** A launcher band at the foot. Deliberately last: it is what you do when
 *      nothing above needs you.
 *
 * ## Honesty rules this screen keeps
 *
 * Every figure reads "—" when its read FAILED, never 0, and the pipeline bar goes to a dashed
 * empty track rather than drawing a distribution it does not have. A dashboard showing zero for a
 * figure it could not load is lying in the most reassuring way possible. The same rule governs the
 * masthead: a failed queue read says so rather than saying "nothing is waiting on you".
 *
 * ## Where the data comes from
 *
 * The organization, the pipeline counts and the signature queue are RLS-guarded client reads
 * (`api/reads.ts`, ADR 0049): the browser asks PostgREST in the database's own region, as the
 * signed-in user, and Postgres decides what it may see. They came from the api Worker until
 * 2026-09-09, when that Worker was measured at P50 2.4 s from Muscat for a read the database
 * answers in a millisecond. The usage card and the recent list still read the public API — their
 * move is a later slice of the same order — and every write on every screen still does (CLAUDE.md
 * §4: no privileged side doors; a read under RLS is not one).
 */
import {
  ActionCard,
  Badge,
  Button,
  Callout,
  Card,
  EmptyState,
  ErrorState,
  Icon,
  LoadingState,
  Pipeline,
  SectionHeading,
  type IconName,
  type PipelineStage,
  type Tone,
} from '@flowza/ui'
import {
  isEnvelopeStatus,
  tabOfSentEnvelope,
  type EnvelopeStatus,
  type InboxTab,
} from '@flowza/core/envelopes/inbox'
import { useCallback, useEffect, useState } from 'react'
import type { DocumentDateFormat } from '@flowza/core/fill/date-format'
import { formatDay, useOrgDateFormat } from './dates'
import { Link, useNavigate } from 'react-router-dom'
import { ApiError, api, apiConfigured } from '../../api/client'
import { StartBand, StartEmptyState } from './StartBand'
import { openCeremony } from '../../api/ceremony'
import {
  readEnvelopeSummary,
  readMyInbox,
  readOrganization,
  readsConfigured,
  type EnvelopeSummary,
} from '../../api/reads'
import type {
  EnvelopeResource,
  EnvelopeSummaryResource,
  OrganizationCardResource,
  ReceivedEnvelope,
} from '../../api/types'
import { useSession } from '../../auth/session'
import { UsageCard } from './UsageCard'
import { describe as messageOf } from './apiErrors'

/**
 * The pipeline's numbers.
 *
 * A FAILED load stays null — which the pipeline renders as a dash and announces as "not yet
 * available" — rather than falling back to zero. Zero is a claim ("you have nothing waiting"); a
 * dash is the absence of one, and on an action inbox the difference is whether somebody goes
 * looking for work they have. The org card already carries the retry for a broken API, so this
 * does not add a second.
 */
type InboxView =
  | { readonly kind: 'pending' }
  | { readonly kind: 'ready'; readonly summary: EnvelopeSummary }
  | { readonly kind: 'failed' }

type OrgView =
  | { readonly kind: 'unconfigured' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly org: OrganizationCardResource }
  | { readonly kind: 'error'; readonly message: string }

type RecentView =
  | { readonly kind: 'unconfigured' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly envelopes: readonly EnvelopeResource[] }
  | { readonly kind: 'error'; readonly message: string }

/**
 * The organization's templates, for the launcher card that has always described them in the
 * abstract.
 *
 * `forbidden` is its own state rather than an error: `templates.create` is seeded to member and up,
 * so a viewer or a billing_admin reaches this screen and legitimately cannot list templates. A red
 * banner for a permission working exactly as designed would be the console shouting at somebody
 * about a door that was never theirs.
 */
type TemplatesView =
  | { readonly kind: 'unconfigured' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly templates: readonly TemplateSummary[] }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'error'; readonly message: string }

/** As `GET /v1/templates` serializes one. `usable` is false with no published version. */
type TemplateSummary = {
  readonly id: string
  readonly name: string
  readonly usable: boolean
  readonly updated_at: string
}

/**
 * Whether this person has adopted a signature — `GET /v1/profile/signature`, permission `null`.
 *
 * Three states and not two, because the checklist step it feeds must be able to say "I do not
 * know". `unknown` is what a failed read produces, and it renders as a dash rather than as an
 * unticked box: an unticked box is a claim that the step is NOT done.
 */
type SignatureView = 'loading' | 'adopted' | 'none' | 'unknown'

/** The documents addressed to this person, which is a different question from what they sent. */
type QueueView =
  | { readonly kind: 'unconfigured' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly items: readonly ReceivedEnvelope[] }
  | { readonly kind: 'error'; readonly message: string }

const ORG_TYPE: Record<OrganizationCardResource['type'], string> = {
  personal: 'Personal',
  team: 'Team',
  isv_parent: 'Partner',
  isv_child: 'Partner-managed',
}

/**
 * A row says exactly what happened to ONE envelope; a tab says what a pile of them have in common.
 *
 * So the label comes from the status and the colour comes from its tab. "Declined / expired" is the
 * right name for a tab that holds both and the wrong name for a row where we know which it was —
 * and taking the tone from `tabOfSentEnvelope` is what keeps the two consistent anyway: every
 * envelope filed under `closed` is red whatever word the row prints.
 */
const STATUS_LABEL: Record<EnvelopeStatus, string> = {
  draft: 'Draft',
  sent: 'Out for signature',
  in_progress: 'In progress',
  correcting: 'Paused for changes',
  completed: 'Completed',
  sealed: 'Sealed',
  declined: 'Declined',
  expired: 'Expired',
  voided: 'Voided',
}

const TAB_TONE: Record<InboxTab, 'neutral' | 'success' | 'warning' | 'danger'> = {
  action_required: 'warning',
  waiting: 'warning',
  drafts: 'neutral',
  completed: 'success',
  closed: 'danger',
  archived: 'neutral',
  deleted: 'neutral',
}

/** The left-edge rail on a recent row: the same tone the badge carries, as a shape. */
const RAIL_TONE: Record<InboxTab, string> = {
  action_required: 'bg-warning',
  waiting: 'bg-warning',
  drafts: 'bg-border-strong',
  completed: 'bg-success',
  closed: 'bg-danger',
  archived: 'bg-border-strong',
  deleted: 'bg-border-strong',
}

const LINK = 'text-accent inline-flex items-center gap-1 font-medium hover:underline'

/**
 * The tinted icon chip, as `@flowza/ui` paints it.
 *
 * A local copy because `CHIP_TONE` is private to `layout.tsx` and the two places that want it here
 * are a step marker and a launcher tile, neither of which is a `StatTile` or an `ActionCard`. Held
 * to the same five tones by the `Record<Tone, string>` annotation, so a tone added there fails to
 * compile here until it is added here too — which is the whole reason to type it rather than to
 * widen it to a plain object.
 */
const CHIP: Record<Tone, string> = {
  neutral: 'bg-surface-sunken text-text-muted',
  accent: 'bg-accent/10 text-accent',
  warning: 'bg-warning/12 text-warning',
  success: 'bg-success/12 text-success',
  danger: 'bg-danger/12 text-danger',
}
/** The stretched link that makes a whole pipeline stage clickable rather than just its chevron. */
const STAGE_LINK = 'absolute inset-0 rounded-lg'

/** "Tuesday, 9 September 2026", in the reader's own locale rather than in ours. */
function today(): string {
  return new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

/**
 * The most recent thing that actually happened to an envelope, and the word for it.
 *
 * Not `updated_at`: the API does not serve one, and inventing "last activity" from `created_at`
 * alone would date a three-week-old conversation to the day somebody typed its subject. These three
 * timestamps are facts the resource carries, and the label names WHICH of them is being shown, so a
 * date on this list is never ambiguous about what it is the date of.
 */
function lastActivity(envelope: EnvelopeResource): { readonly at: string; readonly what: string } {
  if (envelope.completed_at !== null) return { at: envelope.completed_at, what: 'completed' }
  if (envelope.sent_at !== null) return { at: envelope.sent_at, what: 'sent' }
  return { at: envelope.created_at, what: 'created' }
}

/**
 * A day in the ORGANIZATION's shape (ADR 0132), not the browser's.
 *
 * This was `{ day: 'numeric', month: 'short', year: 'numeric' }` — a perfectly good shape that
 * happened to be a fourth one, beside the Inbox's medium dates, the template list's bare locale
 * dates and two `slice(0, 10)` ISO stamps. One organization could see a day written four ways in
 * one session; `dates.ts` says why that is the defect rather than four small style choices.
 */
const dayOf = (iso: string, format: DocumentDateFormat): string => formatDay(iso, format)

/**
 * How long is left, in whole days, or null when there is no deadline.
 *
 * Whole days rather than hours: an envelope that expires in nineteen hours should say "tomorrow"
 * and not "in 19 hours", because the number that matters to somebody deciding whether to deal with
 * it now is which day it dies on. Negative means it already has, which the queue renders as its own
 * state rather than as "in -2 days".
 */
function daysLeft(iso: string | null): number | null {
  if (iso === null) return null
  const ms = new Date(iso).getTime() - Date.now()
  return Number.isFinite(ms) ? Math.ceil(ms / 86_400_000) : null
}

function expiryPhrase(iso: string | null): string | null {
  const days = daysLeft(iso)
  if (days === null) return null
  if (days < 0) return 'expired'
  if (days === 0) return 'expires today'
  if (days === 1) return 'expires tomorrow'
  return `expires in ${days} days`
}

export default function Dashboard() {
  const session = useSession()
  const [org, setOrg] = useState<OrgView>(
    readsConfigured() ? { kind: 'loading' } : { kind: 'unconfigured' },
  )
  const [attempt, setAttempt] = useState(0)
  const [inbox, setInbox] = useState<InboxView>({ kind: 'pending' })
  const [queue, setQueue] = useState<QueueView>(
    readsConfigured() ? { kind: 'loading' } : { kind: 'unconfigured' },
  )
  const [recent, setRecent] = useState<RecentView>(
    apiConfigured() ? { kind: 'loading' } : { kind: 'unconfigured' },
  )
  const [templates, setTemplates] = useState<TemplatesView>(
    apiConfigured() ? { kind: 'loading' } : { kind: 'unconfigured' },
  )
  const navigate = useNavigate()
  const [signature, setSignature] = useState<SignatureView>(apiConfigured() ? 'loading' : 'unknown')
  // The profile wizard's own row (Prompt 5). Null while loading OR if it could not be read, and
  // `GettingStarted` renders both as `unknown` — the same rule the signature row already states.
  const [profilePercent, setProfilePercent] = useState<number | null>(null)

  // The org the token names. Null while the session is still resolving; under RequireSession it
  // is set by the time this renders, and the effect re-runs the moment it arrives regardless.
  const orgId = session.kind === 'signed_in' ? session.orgId : null

  useEffect(() => {
    if (!readsConfigured() || orgId === null) return
    let cancelled = false
    setOrg({ kind: 'loading' })
    readOrganization(orgId)
      .then((loaded) => {
        if (!cancelled) setOrg({ kind: 'ready', org: loaded })
      })
      .catch((error: unknown) => {
        if (!cancelled) setOrg({ kind: 'error', message: messageOf(error) })
      })
    return () => {
      cancelled = true
    }
  }, [attempt, orgId])

  /**
   * The profile's completeness, and the one-time hop into the wizard for somebody who just joined.
   *
   * The redirect is HERE rather than in `provision.tsx` because that component unmounts the moment
   * the refreshed session re-renders the gate above it, and a navigation issued from inside it
   * races that re-render. The flag is read once and cleared, so a refresh does not send somebody
   * back into a wizard they have already skipped.
   */
  useEffect(() => {
    if (!apiConfigured()) return
    let cancelled = false
    let joined = false
    try {
      joined = window.sessionStorage.getItem('flowza.just_joined') === '1'
      window.sessionStorage.removeItem('flowza.just_joined')
    } catch {
      // No storage: no redirect, and the row below is how they find the wizard.
    }
    api<{ readonly completeness?: { readonly percent: number } }>('/v1/profile')
      .then((profile) => {
        if (cancelled) return
        const percent = profile.completeness?.percent ?? null
        setProfilePercent(percent)
        if (joined && percent !== null && percent < 100) navigate('/app/welcome')
      })
      .catch(() => {
        // Silent. A dashboard that showed an error banner because it could not compute a nudge
        // would be louder than the nudge.
      })
    return () => {
      cancelled = true
    }
  }, [navigate])

  // Separate from the org effect on purpose: these are independent requests, and a dashboard that
  // waited for all of them would show nothing until the slowest landed.
  useEffect(() => {
    if (!readsConfigured()) return
    let cancelled = false
    setInbox({ kind: 'pending' })
    readEnvelopeSummary()
      .then((summary) => {
        if (!cancelled) setInbox({ kind: 'ready', summary })
      })
      .catch(() => {
        // Deliberately not surfaced as an error banner. The counts are an aid, not the page, and a
        // red box over four dashes would be louder than the fact it reports.
        if (!cancelled) setInbox({ kind: 'failed' })
      })
    return () => {
      cancelled = true
    }
  }, [attempt])

  /**
   * The signature queue — `public.my_inbox()`, the RECEIVED side.
   *
   * This is the read that makes the masthead a sentence rather than a scoreboard, and it is a
   * different question from the pipeline counts beside it: those count what this organization SENT,
   * this counts what was sent TO this person's confirmed account email, across organizations they
   * frequently do not belong to. Whose turn it is arrives already decided by
   * `app_private.inbox_for_me` — evaluating an envelope's routing across every other recipient is
   * not something this browser can see or be trusted to compute.
   *
   * A failure is surfaced, unlike the pipeline's. The difference is what the reader would conclude
   * from silence: an empty pipeline bar is obviously an absence of information, while "nothing is
   * waiting for your signature" is a specific and reassuring claim, and making it from a failed read
   * is how somebody misses a contract.
   */
  useEffect(() => {
    if (!readsConfigured()) return
    let cancelled = false
    setQueue({ kind: 'loading' })
    readMyInbox()
      .then((items) => {
        if (!cancelled) setQueue({ kind: 'ready', items })
      })
      .catch((error: unknown) => {
        if (!cancelled) setQueue({ kind: 'error', message: messageOf(error) })
      })
    return () => {
      cancelled = true
    }
  }, [attempt])

  /**
   * The five most recent envelopes, from the public API.
   *
   * It is the slowest thing on the page and it must not hold up the ones that are fast. It is also
   * the read most obviously next in line for the ADR 0049 treatment — `GET /v1/envelopes` is a
   * Worker hop to Amsterdam for a list Postgres has in Mumbai — but moving it needs a view that
   * carries the handler's own `envelopes.read_all_org` rule into SQL, the way
   * `v_envelope_status_counts` already does. Until that view exists, reading `envelopes` straight
   * from the browser would show a member every envelope in the org, which is a wider answer than
   * the API gives. So: the slow, correct door.
   */
  const loadRecent = useCallback(() => {
    if (!apiConfigured()) return () => {}
    let cancelled = false
    setRecent({ kind: 'loading' })
    api<{ readonly data: readonly EnvelopeResource[] }>('/v1/envelopes?limit=5')
      .then((page) => {
        if (!cancelled) setRecent({ kind: 'ready', envelopes: page.data })
      })
      .catch((error: unknown) => {
        if (!cancelled) setRecent({ kind: 'error', message: messageOf(error) })
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => loadRecent(), [loadRecent, attempt])

  /**
   * The organization's templates, for the launcher card and the first-run checklist.
   *
   * `ux.md` §6.1 names "recent templates" as zone 3 of this screen and the card has always
   * described templates in the abstract while naming none of them — which is what "templates is
   * missing" looks like from the landing page even when the feature is built.
   *
   * A 403 is NOT an error here. `templates.create` is member and up, so a viewer reaching this
   * screen cannot list them, and that is the permission working. It falls back to the card's own
   * prose rather than to a red box.
   */
  useEffect(() => {
    if (!apiConfigured()) return
    let cancelled = false
    setTemplates({ kind: 'loading' })
    api<{ readonly data: readonly TemplateSummary[] }>('/v1/templates')
      .then((page) => {
        if (!cancelled) setTemplates({ kind: 'ready', templates: page.data })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        if (error instanceof ApiError && error.status === 403) setTemplates({ kind: 'forbidden' })
        else setTemplates({ kind: 'error', message: messageOf(error) })
      })
    return () => {
      cancelled = true
    }
  }, [attempt])

  /**
   * Whether a signature has been adopted — the one setup step this screen can actually PROVE.
   *
   * The checklist used to be a constant with step one hardcoded true, so an organization that had
   * uploaded, placed fields and sent a hundred envelopes still read "1 of 4 steps done". Every step
   * now comes from a read, and a step whose read failed reads as a dash rather than as unticked —
   * an unticked box is a claim that the step is not done, which is a different thing from not
   * knowing.
   */
  useEffect(() => {
    if (!apiConfigured()) return
    let cancelled = false
    setSignature('loading')
    api<{ readonly signature: unknown }>('/v1/profile/signature')
      .then((body) => {
        if (!cancelled) setSignature(body.signature === null ? 'none' : 'adopted')
      })
      .catch(() => {
        if (!cancelled) setSignature('unknown')
      })
    return () => {
      cancelled = true
    }
  }, [attempt])

  const count = (key: keyof EnvelopeSummaryResource): number | null =>
    inbox.kind === 'ready' ? inbox.summary.counts[key] : null

  /**
   * One status's count, or null when the read failed.
   *
   * A status the view returned no row for is genuinely ZERO, not unknown — the view counts rows,
   * and a status with no envelopes produces no row. That distinction is the whole reason this
   * returns 0 for a missing row and null only for a failed read.
   */
  const statusCount = (status: EnvelopeStatus): number | null =>
    inbox.kind === 'ready'
      ? (inbox.summary.byStatus.find((row) => row.status === status)?.count ?? 0)
      : null

  /** Envelopes in a status this bundle has no vocabulary for — the database is ahead of us. */
  const unrecognised = inbox.kind === 'ready' ? inbox.summary.counts.unknown : null

  const email = session.kind === 'signed_in' ? session.email : null

  /**
   * A brand-new organization: every read SUCCEEDED and every one came back empty.
   *
   * This is a DIFFERENT SCREEN, not the same screen with zeros in it, and the distinction is the
   * whole point. Rendered as the normal dashboard, a new tenant meets four huge noughts as the
   * visual centrepiece, three stacked "nothing here" panels down the left, and a pipeline bar with
   * nothing in it — a page whose dominant message is that the product is empty. Nothing on it is
   * false; it is simply the wrong page for somebody who has not started yet.
   *
   * `kind === 'ready'` on all three is load-bearing. A FAILED read must never look like an empty
   * one: an org with nine envelopes out for signature and a broken connection would otherwise be
   * shown a cheerful "let's send your first document". Failure keeps the normal screen, where the
   * dashes say what is actually going on.
   */
  const firstRun =
    inbox.kind === 'ready' &&
    inbox.summary.counts.needs_you === 0 &&
    inbox.summary.counts.waiting === 0 &&
    inbox.summary.counts.drafts === 0 &&
    inbox.summary.counts.completed === 0 &&
    recent.kind === 'ready' &&
    recent.envelopes.length === 0 &&
    queue.kind === 'ready' &&
    actionable(queue.items).length === 0

  /**
   * The stages, in the order an envelope moves through them.
   *
   * Drafts → out for signature → stuck → done. That is a lifecycle, and it is why the bar reads as
   * a pipeline rather than as four bars that happen to be adjacent. `needs_you` is third rather
   * than first even though it is the most urgent: an envelope only lands there after it was sent.
   */
  const stages: readonly PipelineStage[] = [
    {
      key: 'drafts',
      label: 'Drafts',
      value: count('drafts'),
      hint: 'Not yet sent',
      tone: 'neutral',
      link: (
        <Link to="/app/inbox?tab=drafts" className={STAGE_LINK}>
          <span className="visually-hidden">Open your drafts</span>
        </Link>
      ),
    },
    {
      key: 'waiting',
      label: 'Waiting on others',
      value: count('waiting'),
      hint: 'Out for signature',
      tone: 'warning',
      link: (
        <Link to="/app/inbox?tab=waiting" className={STAGE_LINK}>
          <span className="visually-hidden">Open the envelopes you are waiting on</span>
        </Link>
      ),
    },
    {
      key: 'needs_you',
      label: 'Needs your action',
      value: count('needs_you'),
      // NOT "sign, approve, fix a bounce", which is what this said while it showed a dash. A signer
      // is identified by email and carries no user id, so nothing joins your session to a signature
      // request; and a bounce lives on a delivery row, not on the envelope. This counts what it can
      // prove: the envelopes you sent that stopped and need you to decide.
      hint: 'Declined, expired or being corrected',
      tone: 'accent',
      // The one stage with no `?tab=`, and the reason is worth writing down. `@flowza/core` keeps
      // two deliberately different vocabularies: the COUNT bucket puts declined, expired and
      // correcting together as "stuck work", while the LIST files a terminal envelope under how it
      // ended, so those three land across `closed` AND `action_required`. There is no single tab
      // holding exactly what this counted, so it opens the inbox whole rather than a tab that would
      // contradict its own figure.
      link: (
        <Link to="/app/inbox" className={STAGE_LINK}>
          <span className="visually-hidden">Open your inbox</span>
        </Link>
      ),
    },
    {
      key: 'completed',
      label: 'Completed',
      value: count('completed'),
      // Was "Signed and sealed". This folds `sealed` in with `completed`, and no envelope on this
      // cell can be sealed at all — the signer container has no timestamp authority configured, so
      // nothing has ever reached `sealed`. It reads 0 today, which is why that was a latent
      // falsehood rather than a wrong number, and it would have gone live on the first completion.
      hint: 'Signed by everyone',
      tone: 'success',
      link: (
        <Link to="/app/inbox?tab=completed" className={STAGE_LINK}>
          <span className="visually-hidden">Open your completed envelopes</span>
        </Link>
      ),
    },
  ]

  if (firstRun) {
    return (
      <div className="flex flex-col gap-8">
        <Masthead date={today()} email={email} queue={queue} firstRun />
        {/*
          The Start band at the HEAD on the first-run screen, where the four-card launcher used to
          sit second. A person who has never sent anything wants the sequence and then the door;
          `FirstEnvelope` is the sequence, and this is now one band rather than four cards, two of
          which pointed at the same route.
        */}
        <StartBand />
        {/*
          `FirstEnvelope` (ADR 0055's three-step card) already occupies the first-run slot and
          carries the same sentence, so `StartEmptyState` is NOT rendered here — two panels headed
          "Send your first document for signature" on one screen is worse than either alone. It
          lands on the recent-envelopes empty state below, which is the other "no envelopes yet".
        */}
        <FirstEnvelope />
        <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <div className="min-w-0">
            <Launcher templates={templates} width="narrow" />
          </div>
          <div className="flex min-w-0 flex-col gap-6">
            {apiConfigured() ? <UsageCard /> : null}
            <OrganizationCard org={org} onRetry={() => setAttempt((n) => n + 1)} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-8">
      <Masthead date={today()} email={email} queue={queue} />

      <StartBand />

      <section aria-labelledby="status-heading">
        <h2 id="status-heading" className="visually-hidden">
          What is waiting on you
        </h2>
        <div className="border-card-border bg-card-bg shadow-raised rounded-xl border p-5">
          <div className="mb-5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <h3 className="text-text-primary m-0 text-base font-semibold tracking-tight">
              Your pipeline
            </h3>
            <p className="text-text-muted m-0 text-sm">
              Everything this organization has sent, by where it stopped.
            </p>
          </div>
          <Pipeline
            stages={stages}
            caption={
              <>
                Counted over the envelopes you can see. A dash means the count could not be loaded,
                which is not the same as zero. Voided envelopes are counted in none of these.
              </>
            }
          />
          <StatusBreakdown countOf={statusCount} unrecognised={unrecognised} />
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="flex min-w-0 flex-col gap-8">
          <section aria-labelledby="signature-queue">
            <SectionHeading
              id="signature-queue"
              title="Waiting for your signature"
              description="Documents other people sent to your account's email address"
              action={
                // NOT "Open your inbox": the pipeline's "Needs your action" stage already carries a
                // stretched link by that name, and two links with one accessible name pointing at
                // the same place is a screen reader reading the same destination twice and a test
                // that cannot say which one it meant.
                <Link to="/app/inbox" className={LINK}>
                  View your full inbox
                  <Icon name="arrow-right" size={14} />
                </Link>
              }
            />
            <SignatureQueue view={queue} onRetry={() => setAttempt((n) => n + 1)} />
          </section>

          <section aria-labelledby="recent">
            <SectionHeading
              id="recent"
              title="Recent envelopes"
              // Says WHICH five out loud. `GET /v1/envelopes` accepts only a limit and an opaque
              // cursor — no status filter and no sort — so calling a page of five "the soonest to
              // expire" would be a claim the API cannot support.
              description="The last five you created, newest first, whatever state they are in."
              action={
                <Link to="/app/documents" className={LINK}>
                  View all
                  <Icon name="arrow-right" size={14} />
                </Link>
              }
            />
            <RecentEnvelopes view={recent} onRetry={loadRecent} />
          </section>

          {/*
            In the WORK column, not the rail. It was in the rail, which put roughly 500 px of dead
            space under the recent list at 1440 px — the rail ran half a screen longer than the
            column it flanks — and it is the wrong reading anyway: a checklist of what to do next is
            work, and the rail is for context that must never outrank it.
          */}
          <GettingStarted counts={count} signature={signature} profilePercent={profilePercent} />
        </div>

        <div className="flex min-w-0 flex-col gap-6">
          {/*
            §11 makes this a requirement on every plan, Free included, and puts it where the work
            happens rather than behind a billing menu nobody opens until an invoice surprises them.
          */}
          {apiConfigured() ? <UsageCard /> : null}

          <OrganizationCard org={org} onRetry={() => setAttempt((n) => n + 1)} />
        </div>
      </div>

      {/*
        The launcher, at the FOOT and not the head. Starting something new is what you do when
        nothing above needs you, and a landing page that opens with four "create" tiles is a page
        that has decided the reader has no outstanding work — which is exactly the assumption the
        masthead exists to stop making.

        The Start BAND is the exception and sits at the head (below the masthead): it is one row,
        not a grid of tiles, and DocuSign's own placement is what a reader arriving from there
        expects. The launcher stays below it because it names real templates and the inbox, which
        the band does not.
      */}
      <Launcher templates={templates} />
    </div>
  )
}

/* ------------------------------------------------------------------------------ masthead ---- */

/** The received envelopes that are actually on this person, most urgent first. */
function actionable(items: readonly ReceivedEnvelope[]): readonly ReceivedEnvelope[] {
  return [...items]
    .filter((item) => item.bucket === 'action_required')
    .sort((a, b) => {
      // Your turn beats not-your-turn; then the one that dies soonest. An envelope with no expiry
      // sorts last rather than first — no deadline is the absence of urgency, not the maximum of it.
      if (a.my_turn !== b.my_turn) return a.my_turn ? -1 : 1
      const left = daysLeft(a.expires_at)
      const right = daysLeft(b.expires_at)
      if (left === right) return 0
      if (left === null) return 1
      if (right === null) return -1
      return left - right
    })
}

/**
 * The top of the page: the date, the reader, and the one sentence that decides their next minute.
 *
 * The sentence is computed from the SIGNATURE QUEUE and it is careful about which of four things it
 * is allowed to say. "Nothing is waiting for your signature" is a claim, and it is only made from a
 * successful read of an empty queue; a failed or unconfigured read says that instead, in words,
 * because a reassuring sentence assembled from a broken query is how somebody misses a contract.
 */
function Masthead({
  date,
  email,
  queue,
  firstRun = false,
}: {
  readonly date: string
  readonly email: string | null
  readonly queue: QueueView
  /** True only when every read succeeded and every one was empty — see `firstRun` on the page. */
  readonly firstRun?: boolean
}) {
  const waiting = queue.kind === 'ready' ? actionable(queue.items).length : null

  return (
    <header className="border-card-border bg-card-bg shadow-raised relative overflow-hidden rounded-2xl border">
      {/*
        A wash rather than a painted banner. A saturated masthead is the thing every SaaS console
        did in 2016 and it fights the content underneath it for the reader's eye; a low-alpha
        gradient in the accent says "top of the page" and then gets out of the way. It follows a
        tenant's accent, because it is chrome and not the mark (packages/ui/src/components/brand.tsx
        says why the mark does not).
      */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_150%_at_0%_0%,var(--accent),transparent_62%)] opacity-[0.09]"
      />
      <div className="relative flex flex-wrap items-start justify-between gap-x-8 gap-y-5 p-6 sm:p-7">
        <div className="min-w-0 flex-1">
          <p className="text-text-muted m-0 text-xs font-medium uppercase tracking-wide">{date}</p>
          <h1 className="text-text-primary m-0 mt-1.5 text-3xl font-semibold tracking-tight">
            {/* "Welcome back" is a small lie to somebody who has never been here. */}
            {firstRun ? 'Welcome to FlowZa Sign' : 'Welcome back'}
          </h1>

          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
            {firstRun ? (
              // On a first run the queue IS empty and the read DID succeed, so "nothing is waiting
              // for your signature" would be true and useless — it answers a question nobody with
              // an empty account is asking. Say what is actually true of the organization instead.
              <p className="text-text-muted m-0 text-lg">
                Nothing has been sent from this organization yet.
              </p>
            ) : queue.kind === 'loading' ? (
              <p className="text-text-muted m-0 text-lg">Checking what needs your signature…</p>
            ) : queue.kind === 'error' || queue.kind === 'unconfigured' ? (
              <p className="text-text-muted m-0 inline-flex items-center gap-2 text-lg">
                <Icon name="circle-alert" size={18} className="text-warning" />
                Your signing queue could not be read, so this cannot say whether anything is
                waiting.
              </p>
            ) : waiting === 0 ? (
              <p className="text-text-muted m-0 inline-flex items-center gap-2 text-lg">
                <Icon name="circle-check" size={18} className="text-success" />
                Nothing is waiting for your signature.
              </p>
            ) : (
              <>
                <p className="text-text-primary m-0 text-lg font-medium">
                  {waiting} document{waiting === 1 ? '' : 's'} need
                  {waiting === 1 ? 's' : ''} your signature.
                </p>
                <a href="#signature-queue" className={LINK}>
                  Review {waiting === 1 ? 'it' : 'them'}
                  <Icon name="arrow-right" size={14} />
                </a>
              </>
            )}
          </div>

          {email !== null ? (
            <p className="text-text-muted m-0 mt-4 inline-flex items-center gap-1.5 text-xs">
              <Icon name="lock" size={12} />
              Signed in as {email}
            </p>
          ) : (
            <p className="text-text-muted m-0 mt-4 text-xs">Signed in</p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Link
            to="/app/documents"
            className="bg-accent text-text-on-accent hover:bg-accent-hover shadow-raised ease-standard h-control inline-flex items-center gap-1.5 rounded-md px-3.5 text-sm font-medium transition duration-150"
          >
            <Icon name="upload" size={16} />
            Upload a document
          </Link>
        </div>
      </div>
    </header>
  )
}

/* ----------------------------------------------------------------------- signature queue ---- */

/**
 * The documents on this person, and the button that opens them.
 *
 * Three at most, newest deadline first, with the way to the rest underneath. The cap is the point:
 * this is a dashboard panel, not the inbox, and a landing page that renders forty rows has become
 * the screen it was meant to summarise. What it must NOT do is become a teaser — the button here
 * opens the ceremony, exactly as the inbox's does, because a count that makes somebody navigate to
 * press a button is the scoreboard this screen is written against.
 */
function SignatureQueue({
  view,
  onRetry,
}: {
  readonly view: QueueView
  readonly onRetry: () => void
}) {
  if (view.kind === 'unconfigured') {
    return (
      <EmptyState
        // Deliberately NOT the organization card's wording. Both say the same thing about the same
        // missing configuration, and two identical headings on one screen read as one message
        // rendered twice — which is also how a test that means to find the org card finds this.
        title="Your signing queue cannot be read here"
        body="This build has no Supabase URL or publishable key, so nothing addressed to you can be loaded."
      />
    )
  }
  if (view.kind === 'loading') return <LoadingState label="Loading your signing queue" />
  if (view.kind === 'error') {
    return (
      <ErrorState title="Your signing queue could not load" body={view.message} onRetry={onRetry} />
    )
  }

  const items = actionable(view.items)
  if (items.length === 0) {
    return (
      <EmptyState
        title="Nothing is waiting for your signature"
        body="Documents sent to your account's email address appear here the moment they arrive."
      />
    )
  }

  return (
    <div className="border-card-border bg-card-bg shadow-raised overflow-hidden rounded-xl border">
      <ul className="m-0 flex list-none flex-col p-0">
        {items.slice(0, 3).map((item) => (
          <QueueRow key={item.envelope_signer_id} item={item} />
        ))}
      </ul>
      {items.length > 3 ? (
        <div className="border-border-subtle bg-surface-sunken/50 border-t px-4 py-2.5 text-sm">
          <Link to="/app/inbox" className={LINK}>
            {items.length - 3} more waiting on you
            <Icon name="arrow-right" size={14} />
          </Link>
        </div>
      ) : null}
    </div>
  )
}

function QueueRow({ item }: { readonly item: ReceivedEnvelope }) {
  const [opening, setOpening] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const expiry = expiryPhrase(item.expires_at)
  const days = daysLeft(item.expires_at)
  // "Soon" is three days, and it is a display threshold rather than a policy one: nothing in the
  // product behaves differently, this only decides whether the phrase is drawn in the warning tone.
  const urgent = days !== null && days <= 3

  return (
    <li className="border-border-subtle flex flex-col gap-3 border-b px-4 py-3.5 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-center gap-3">
        {/*
          The sending organization's initial, not an avatar. There is no logo for a counterparty
          org and inventing a generic person glyph for what is usually a COMPANY would be worse than
          a letter — the letter at least carries the one fact the row is keyed on.
        */}
        <span
          aria-hidden="true"
          className="bg-accent/10 text-accent flex size-9 shrink-0 items-center justify-center rounded-lg text-sm font-semibold"
        >
          {(item.sender_org.trim()[0] ?? '?').toUpperCase()}
        </span>
        <div className="min-w-0">
          <p className="text-text-primary m-0 truncate font-medium">{item.subject}</p>
          <p className="text-text-muted m-0 mt-0.5 truncate text-xs">
            {/* The sender's organization, named because it is frequently not the reader's own —
                which is the fact that makes this list different from every other list here. */}
            From {item.sender_org}
            {item.signer_kind === 'cc' ? ' · you were copied in' : ''}
            {expiry === null ? null : (
              <>
                {' · '}
                <span className={urgent ? 'text-warning font-medium' : ''}>{expiry}</span>
              </>
            )}
          </p>
          {error === null ? null : (
            <p className="text-danger m-0 mt-1.5 text-sm" role="alert">
              {error}
            </p>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3 self-end sm:self-auto">
        {item.my_turn ? (
          <Button
            onClick={() => {
              setError(null)
              setOpening(true)
              openCeremony(item.envelope_signer_id, 'dashboard').catch((e: unknown) => {
                setOpening(false)
                setError(messageOf(e))
              })
            }}
            disabled={opening}
          >
            {/* ADR 0157: only a `signer` signs. `=== 'signer'` rather than a list of the other
                three, so a kind this bundle does not know reads as the neutral label — the same
                direction the ceremony's own title takes. */}
            {opening
              ? 'Opening…'
              : item.signer_kind === 'signer'
                ? 'Review & sign'
                : 'Review & complete'}
          </Button>
        ) : (
          <Badge tone="neutral">Waiting on someone else</Badge>
        )}
      </div>
    </li>
  )
}

/* ------------------------------------------------------------------- recent envelopes ------- */

/**
 * The last five envelopes, as a table.
 *
 * A table rather than a stack of cards because every column here is a comparison — which of these
 * is furthest along, which has the most people on it, which moved most recently — and a card grid
 * is the layout that makes comparison hardest. (It is also the shape the competitor reaches for:
 * Transaction Rooms renders its transaction list as a grid of photograph tiles, which is beautiful
 * and answers none of those three questions.) It ships all four states it can actually be in
 * (ux.md §12): a list that quietly renders nothing when its read failed is the failure this whole
 * screen is written against.
 */
function RecentEnvelopes({
  view,
  onRetry,
}: {
  readonly view: RecentView
  readonly onRetry: () => void
}) {
  if (view.kind === 'unconfigured') {
    return (
      <EmptyState
        title="No API configured for this deployment"
        body="This build has no VITE_API_URL, so the envelope list cannot be read."
      />
    )
  }
  if (view.kind === 'loading') return <LoadingState label="Loading your recent envelopes" />
  if (view.kind === 'error') {
    return (
      <ErrorState title="Recent envelopes could not load" body={view.message} onRetry={onRetry} />
    )
  }
  if (view.envelopes.length === 0) {
    return <StartEmptyState />
  }

  return (
    <div className="border-card-border bg-card-bg shadow-raised overflow-hidden rounded-xl border">
      {/* The one place on this page allowed to scroll sideways: five columns do not fold onto a
          phone without either truncating a subject to nothing or stacking into an unscannable
          list. */}
      <div className="overflow-x-auto">
        {/*
          `table-fixed` with declared widths, not `auto`. An auto table sizes to its content, so one
          long subject pushed "Last activity" off the right-hand edge of the column this table sits
          in — measured at 1440 px, where the header read "LAST AC". Fixed widths plus a truncating
          subject means the layout is decided by the design rather than by the data.

          `min-w-[34rem]` is the other half of that bargain and is NOT optional: below it the three
          fixed columns exceed the table's width, the auto column collapses to nothing, and the
          cells draw over each other — on a 390 px phone the two headers rendered as "ENVELUSPE".
          With a floor, the wrapper's `overflow-x-auto` scrolls instead, which is the one place on
          this page allowed to.
        */}
        <table className="w-full min-w-[34rem] table-fixed border-collapse text-sm">
          <colgroup>
            {/* 3px of rail, then the subject. */}
            <col className="w-[3px]" />
            <col />
            <col className="w-[9.5rem]" />
            {/* 8.5rem holds "Sep 8, 2026" — about 5.5rem of Inter at the base size — with room for
                a longer month name in another locale, and hands the rest back to the subject. */}
            <col className="w-[8.5rem]" />
            <col className="w-12" />
          </colgroup>
          <thead>
            <tr className="border-border-subtle bg-surface-sunken/60 border-b">
              <th className="p-0">
                <span className="visually-hidden">Status colour</span>
              </th>
              <Th>Envelope</Th>
              <Th>Status</Th>
              <Th className="text-right">Last activity</Th>
              <th className="px-3 py-2">
                <span className="visually-hidden">Open</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {view.envelopes.map((envelope) => (
              <EnvelopeRow key={envelope.id} envelope={envelope} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Th({
  children,
  className = '',
}: {
  readonly children: string
  readonly className?: string
}) {
  return (
    <th
      scope="col"
      className={`text-text-muted px-3 py-2 text-left text-xs font-medium uppercase tracking-wide ${className}`}
    >
      {children}
    </th>
  )
}

function EnvelopeRow({ envelope }: { readonly envelope: EnvelopeResource }) {
  const dateFormat = useOrgDateFormat()
  // An unrecognised status is rendered as the raw word rather than mapped to a guess: a status this
  // build has no vocabulary for means the database is ahead of this bundle, and inventing a tone
  // for it would put a wrong colour on a real fact.
  const status: EnvelopeStatus | null = isEnvelopeStatus(envelope.status) ? envelope.status : null
  const tab = status === null ? null : tabOfSentEnvelope(status)
  const activity = lastActivity(envelope)
  // Only while the envelope can still be signed. "Expires in 3 days" under a completed one is
  // noise about a deadline that stopped mattering the moment everybody signed.
  const open = tab === 'waiting' || tab === 'action_required'
  const expiry = open ? expiryPhrase(envelope.expires_at) : null
  const days = daysLeft(envelope.expires_at)
  const urgent = open && days !== null && days <= 3

  return (
    <tr className="border-border-subtle ease-standard hover:bg-surface-sunken/60 border-b transition-colors duration-150 last:border-b-0">
      {/*
        A 3 px rail in the row's own tone, and the reason it is a cell rather than a border is that
        `table-fixed` plus `border-collapse` will not draw a left border on a `<tr>` at all. It
        carries no information the badge does not — it is the badge's colour, made scannable down a
        column, so five rows sort into groups before a word is read.
      */}
      <td aria-hidden="true" className="p-0">
        <div className={`h-full min-h-10 w-[3px] ${tab === null ? '' : RAIL_TONE[tab]}`} />
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-3">
          <span className="bg-surface-sunken text-text-muted flex size-8 shrink-0 items-center justify-center rounded-lg">
            <Icon name="file-text" size={16} />
          </span>
          <div className="min-w-0">
            <Link
              to={`/app/envelopes/${envelope.id}/recipients`}
              className="text-text-primary hover:text-accent block truncate font-medium"
            >
              {envelope.subject}
            </Link>
            {/* Documents and recipients on the subline rather than as two more columns. Four
                columns fit the rail-flanked width this table actually gets; six did not, and a
                count is a detail of the row, not a thing anybody scans a column of.

                Expiry and revision ride here for the same reason. Both are on every row of
                `GET /v1/envelopes` and both were being thrown away, and an envelope about to
                expire is the single highest-value thing a sender can be told. The word and the
                colour together, never the colour alone. */}
            <p className="text-text-muted m-0 flex items-baseline gap-1 text-xs">
              {/*
                The counts truncate; the expiry does NOT. In one truncating run the line ended
                "expires in 3…", and this file already says elsewhere why that is unacceptable: a
                truncated date is a WRONG date, worse than a missing one. Three days and three
                hours are different decisions. So the deadline is `shrink-0` and the countable
                detail gives way to it.
              */}
              <span className="min-w-0 truncate">
                {envelope.counts.documents} document{envelope.counts.documents === 1 ? '' : 's'} ·{' '}
                {envelope.counts.signers} recipient{envelope.counts.signers === 1 ? '' : 's'}
                {envelope.revision_number > 1 ? ` · revision ${envelope.revision_number}` : ''}
              </span>
              {expiry === null ? null : (
                <span className={`shrink-0 ${urgent ? 'text-warning font-medium' : ''}`}>
                  · {expiry}
                </span>
              )}
            </p>
          </div>
        </div>
      </td>
      <td className="px-3 py-3">
        {status === null || tab === null ? (
          <Badge tone="neutral">{envelope.status}</Badge>
        ) : (
          <Badge tone={TAB_TONE[tab]} dot>
            {STATUS_LABEL[status]}
          </Badge>
        )}
      </td>
      <td className="py-3 pl-3 pr-1 text-right">
        {/*
          Date first, then a caption naming WHICH timestamp it is. Two other shapes were tried and
          both were wrong: the date alone is ambiguous, because a draft's date and a completion's
          date are different facts that looked identical; and "Completed Sep 8, 2026" on one line
          ran to about 160 px and was clipped inside this column at a 1024 px viewport — a
          truncated date is a WRONG date, which is worse than a missing one. Two short lines
          cannot clip.

          The caption is lower case because it is a caption and not a second status: "Completed"
          twice in one row, once as the badge and once here, read as a rendering fault.
        */}
        <time dateTime={activity.at} className="text-text-primary block whitespace-nowrap">
          {dayOf(activity.at, dateFormat)}
        </time>
        <span className="text-text-muted text-xs">{activity.what}</span>
      </td>
      <td className="py-3 pl-0 pr-2 text-right">
        <Link
          to={`/app/envelopes/${envelope.id}/recipients`}
          className="text-text-muted hover:text-accent hover:bg-surface-sunken inline-flex size-8 items-center justify-center rounded-md"
          aria-label={`Open ${envelope.subject}`}
        >
          <Icon name="chevron-right" size={16} />
        </Link>
      </td>
    </tr>
  )
}

/**
 * The organization card: who you are signed in as, at the level that matters for a document.
 *
 * Lifted out of the page body because BOTH dashboards render it — the first-run one and the
 * working one — and a second copy is a second thing to keep in step.
 */
function OrganizationCard({
  org,
  onRetry,
}: {
  readonly org: OrgView
  readonly onRetry: () => void
}) {
  const dateFormat = useOrgDateFormat()
  // No residency row here since 2026-09-14 (testing team: "Data Residency should not be displayed
  // on the tenant login page"). The dashboard used to carry a masked "Data residency" with a Show
  // link; it still answers the question, one place over — Settings › Organization keeps the row,
  // and CLAUDE.md §0's rule is untouched: the label there names the country the bytes are in.
  return (
    <Card
      title="Your organization"
      action={
        org.kind === 'ready' ? (
          <Link to="/app/settings" className={LINK}>
            Settings
            <Icon name="arrow-right" size={14} />
          </Link>
        ) : undefined
      }
    >
      {org.kind === 'unconfigured' ? (
        <Callout tone="info" title="No database configured for this deployment">
          This build has no <code>VITE_SUPABASE_URL</code> or publishable key, so nothing here can
          be read. Set them in the Pages project and redeploy.
        </Callout>
      ) : org.kind === 'loading' ? (
        <LoadingState label="Loading your organization" />
      ) : org.kind === 'error' ? (
        <ErrorState title="Your organization could not load" body={org.message} onRetry={onRetry} />
      ) : (
        <div>
          <div className="flex items-start gap-3">
            <span className="bg-accent/10 text-accent flex size-11 shrink-0 items-center justify-center rounded-xl">
              <Icon name="building" size={20} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-text-primary m-0 truncate font-medium">{org.org.name}</p>
              <p className="text-text-muted m-0 text-sm">
                <code>{org.org.slug}</code>
              </p>
            </div>
            <Badge tone="neutral">{ORG_TYPE[org.org.type]}</Badge>
          </div>
          <dl className="border-border-subtle m-0 mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2.5 border-t pt-4 text-sm">
            <dt className="text-text-muted flex items-center gap-1.5">
              <Icon name="clock" size={14} />
              Created
            </dt>
            <dd className="text-text-primary m-0 text-right font-medium">
              <time dateTime={org.org.created_at}>{formatDay(org.org.created_at, dateFormat)}</time>
            </dd>
          </dl>
        </div>
      )}
    </Card>
  )
}

/* ---------------------------------------------------------------------------- launcher ----- */

/**
 * "Start something" — the four doors, at the foot of the working dashboard and beside the rail on
 * the first-run one.
 *
 * Lifted out of the page body for the same reason as the organization card: both screens render it.
 * On the first-run screen it is the SECOND thing offered, after the three-step card, because a
 * person who has never sent anything wants the sequence before they want the menu.
 */
function Launcher({
  templates,
  width = 'full',
}: {
  readonly templates: TemplatesView
  /**
   * How much room the caller is giving it.
   *
   * Tailwind's breakpoints are VIEWPORT-based, not container-based, so `xl:grid-cols-4` fires on a
   * 1440 px screen even when the grid is inside a 3fr column roughly half that wide — which is how
   * four launcher cards came to render at about 150 px each, one word per line, on the first-run
   * screen. `narrow` is the caller saying "you are in a column, not across the page".
   */
  readonly width?: 'full' | 'narrow'
}) {
  return (
    <section aria-labelledby="quick-actions">
      <SectionHeading
        id="quick-actions"
        title="Start something"
        description="Send a new document, reuse a template, or pick up where you left off."
      />
      <div className={`grid gap-3 sm:grid-cols-2 ${width === 'full' ? 'xl:grid-cols-4' : ''}`}>
        <ActionCard
          icon="upload"
          title="Upload a document"
          body="PDF or Office. It is scanned, converted and made ready to place fields on."
          tone="accent"
        >
          <Link to="/app/documents" className={LINK}>
            Start an envelope
            <Icon name="arrow-right" size={14} />
          </Link>
        </ActionCard>
        <ActionCard
          icon="send"
          title="Send for signature"
          body="Add recipients, set the signing order, then send from the recipients step."
          tone="accent"
        >
          {/*
                `/app/documents`, the same destination as the card above, because there is one list
                and both cards are doors onto it — `{ path: 'documents', element: <Envelopes /> }`.
                This said `/app/envelopes`, which is not a registered route, so the catch-all returned
                the caller to this dashboard: the product's primary send call to action was a loop
                back to where it was pressed. Measured on production, 2026-09-10.
                `test/dead-links.test.ts` now refuses any link with no route.
              */}
          <Link to="/app/documents" className={LINK}>
            Open envelopes
            <Icon name="arrow-right" size={14} />
          </Link>
        </ActionCard>
        <TemplateCard templates={templates} />
        <ActionCard
          icon="inbox"
          title="Sign something yourself"
          body="Documents other people sent to your account's email address land here."
          tone="neutral"
        >
          <Link to="/app/inbox" className={LINK}>
            Go to your inbox
            <Icon name="arrow-right" size={14} />
          </Link>
        </ActionCard>
      </div>
    </section>
  )
}

/**
 * The "Reuse a template" door, naming the templates that actually exist.
 *
 * `ux.md` §6.1 lists "recent templates" as zone 3 of this screen, and this card has described
 * templates in the abstract since it was written while naming none of them — which is exactly what
 * "templates is missing" looks like from the landing page even though the feature is built,
 * shipped and reachable. The names come from `GET /v1/templates`, the same list the Templates
 * screen renders.
 *
 * Four states, and the two unusual ones are the point:
 *
 *   - FORBIDDEN is not an error. `templates.create` is member and up, so a viewer or a
 *     billing_admin lands here and legitimately cannot list templates. It falls back to the card's
 *     own prose, silently, because a red box about a door that was never theirs is the console
 *     shouting at somebody for existing.
 *   - EMPTY says where a template comes from, because the answer is not on this screen and not on
 *     the Templates screen either: a template is saved FROM an envelope, on the recipients step.
 *
 * `usable` is surfaced rather than filtered. A template with no published version cannot be
 * instantiated, and hiding it would leave somebody hunting for a template they know they saved.
 */
function TemplateCard({ templates }: { readonly templates: TemplatesView }) {
  const named =
    templates.kind === 'ready'
      ? [...templates.templates]
          .sort((a, b) => b.updated_at.localeCompare(a.updated_at))
          .slice(0, 3)
      : []

  return (
    <div className="border-card-border bg-card-bg shadow-raised ease-standard focus-within:border-accent hover:border-border-strong hover:shadow-lifted group flex gap-3 rounded-xl border p-4 transition duration-150 motion-safe:hover:-translate-y-0.5">
      <span
        className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${CHIP.success}`}
      >
        <Icon name="template" size={20} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <p className="text-text-primary m-0 font-medium">Reuse a template</p>

        {templates.kind === 'ready' && named.length > 0 ? (
          <ul className="m-0 mt-1.5 flex flex-1 list-none flex-col gap-1 p-0">
            {named.map((template) => (
              <li key={template.id} className="flex min-w-0 items-center gap-1.5">
                <Icon name="file-text" size={12} className="text-text-muted shrink-0" />
                <span className="text-text-muted min-w-0 truncate text-sm">{template.name}</span>
                {template.usable ? null : (
                  <span className="text-text-muted shrink-0 text-xs">(no version)</span>
                )}
              </li>
            ))}
          </ul>
        ) : templates.kind === 'loading' ? (
          <p className="text-text-muted m-0 mt-0.5 flex-1 text-sm">Loading your templates…</p>
        ) : templates.kind === 'error' ? (
          // Named, not swallowed. A card that quietly showed its generic prose when the read failed
          // would be telling somebody with nine templates that the sentence "a template is a saved
          // envelope" was the whole answer.
          <p className="text-text-muted m-0 mt-0.5 flex-1 text-sm">
            Your templates could not be loaded, so this cannot list them.
          </p>
        ) : (
          <p className="text-text-muted m-0 mt-0.5 flex-1 text-sm">
            A template is a saved envelope — the same documents, fields and roles, ready to send.
            You save one from an envelope, on its recipients step.
          </p>
        )}

        <div className="mt-2 text-sm font-medium">
          <Link to="/app/templates" className={LINK}>
            {templates.kind === 'ready' && templates.templates.length > named.length
              ? `All ${templates.templates.length} templates`
              : 'Browse templates'}
            <Icon name="arrow-right" size={14} />
          </Link>
        </div>
      </div>
    </div>
  )
}

/* --------------------------------------------------------------------------- first run ------ */

/**
 * The three steps to a first envelope, for an organization that has none.
 *
 * This card exists because the working dashboard is the wrong page for somebody who has not
 * started. Rendered there, a new tenant meets four noughts and three "nothing here" panels; the
 * product's first impression becomes a list of things it does not have. Here the same facts are a
 * route: the steps are the ones the composer actually walks, named by the labels on the screens
 * they lead to, and the button goes to the first one.
 *
 * The steps are NOT a progress indicator and carry no tick. Nothing here is measured — this card
 * only ever renders when every count is a real zero — and a checklist implying it knows which step
 * you are on would be inventing that. `GettingStarted` is the measured one, and it appears once
 * there is something to measure.
 */
function FirstEnvelope() {
  const steps: ReadonlyArray<{ icon: IconName; title: string; body: string; tone: Tone }> = [
    {
      icon: 'upload',
      title: 'Upload a document',
      body: 'PDF or Office, from Documents. It is scanned, converted and made ready to place fields on.',
      tone: 'accent',
    },
    {
      icon: 'pen',
      title: 'Place the fields',
      body: 'Signatures, initials, dates, text and checkboxes — dropped where they belong on the page.',
      tone: 'warning',
    },
    {
      icon: 'send',
      title: 'Add recipients and send',
      body: 'Name who signs and in what order. The recipients step is where an envelope goes out.',
      tone: 'success',
    },
  ]

  return (
    <section
      aria-labelledby="first-envelope"
      className="border-card-border bg-card-bg shadow-raised relative overflow-hidden rounded-2xl border"
    >
      {/* The same low-alpha accent wash the masthead uses, mirrored to the opposite corner so the
          two bands read as one family without either becoming a painted banner. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_150%_at_100%_0%,var(--accent),transparent_62%)] opacity-[0.09]"
      />
      <div className="relative p-6 sm:p-7">
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
          <div className="min-w-0">
            <h2
              id="first-envelope"
              className="text-text-primary m-0 text-xl font-semibold tracking-tight"
            >
              Send your first document for signature
            </h2>
            <p className="text-text-muted m-0 mt-1 max-w-2xl text-sm">
              Three steps, one screen each. Everything after this — templates, reminders, the audit
              trail — starts from an envelope.
            </p>
          </div>
          <Link
            to="/app/documents"
            className="bg-accent text-text-on-accent hover:bg-accent-hover shadow-raised ease-standard h-control inline-flex shrink-0 items-center gap-1.5 rounded-md px-3.5 text-sm font-medium transition duration-150"
          >
            <Icon name="upload" size={16} />
            Start an envelope
          </Link>
        </div>

        <ol className="m-0 mt-6 grid list-none gap-4 p-0 sm:grid-cols-3">
          {steps.map((step, index) => (
            <li key={step.title} className="min-w-0">
              <div className="flex items-center gap-2.5">
                <span
                  aria-hidden="true"
                  className={`flex size-9 shrink-0 items-center justify-center rounded-lg ${CHIP[step.tone]}`}
                >
                  <Icon name={step.icon} size={18} />
                </span>
                <span className="text-text-muted text-xs font-semibold uppercase tracking-wide">
                  Step {index + 1}
                </span>
              </div>
              <p className="text-text-primary m-0 mt-2.5 font-medium">{step.title}</p>
              <p className="text-text-muted m-0 mt-1 text-sm">{step.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------- status breakdown ------- */

/**
 * The nine real statuses behind the pipeline's four buckets.
 *
 * `readEnvelopeSummary` has always fetched one row per status and folded them to four before the
 * screen saw them, which threw away the difference between four counterparties refusing and four
 * envelopes timing out — opposite problems with opposite responses. This is the same read, not a
 * second one: the rows are handed over rather than dropped.
 *
 * Colour comes from the `chart-*` family, which is the palette this design system reserves for
 * areas of meaning and validates as a SET for colour-vision deficiency (ADR 0051). Six of the seven
 * appear here and appeared nowhere on this screen before — this is where "more colour" comes from
 * honestly, each hue attached to a fact the database measured rather than to decoration.
 *
 * Every chip carries its WORD. The dot is a second encoding, never the only one (ux.md §1's
 * accessibility floor), which is also why a status with nothing in it is simply absent rather than
 * greyed out: a row of zeroes is noise, and the pipeline above already states the totals.
 */
const BREAKDOWN: ReadonlyArray<{
  readonly status: EnvelopeStatus
  readonly label: string
  readonly dot: string
  readonly ink: string
}> = [
  {
    status: 'in_progress',
    label: 'Partly signed',
    dot: 'bg-chart-in-progress',
    ink: 'text-chart-in-progress',
  },
  {
    status: 'correcting',
    label: 'Paused for changes',
    dot: 'bg-chart-rate',
    ink: 'text-chart-rate',
  },
  { status: 'declined', label: 'Declined', dot: 'bg-chart-declined', ink: 'text-chart-declined' },
  { status: 'expired', label: 'Expired', dot: 'bg-chart-expired', ink: 'text-chart-expired' },
  { status: 'sealed', label: 'Sealed', dot: 'bg-chart-completed', ink: 'text-chart-completed' },
  { status: 'voided', label: 'Voided', dot: 'bg-chart-voided', ink: 'text-chart-voided' },
]

function StatusBreakdown({
  countOf,
  unrecognised,
}: {
  readonly countOf: (status: EnvelopeStatus) => number | null
  readonly unrecognised: number | null
}) {
  // A failed read renders nothing at all. The pipeline directly above is already four dashes
  // saying the counts are unknown; a second band of dashes would say it twice and louder.
  const rows = BREAKDOWN.map((row) => ({ ...row, value: countOf(row.status) })).filter(
    (row) => row.value !== null && row.value > 0,
  )
  if (rows.length === 0 && (unrecognised === null || unrecognised === 0)) return null

  return (
    <div className="border-border-subtle mt-4 border-t pt-4">
      <p className="text-text-muted m-0 mb-2.5 text-xs font-medium uppercase tracking-wide">
        Of those, right now
      </p>
      <ul className="m-0 flex list-none flex-wrap gap-x-2 gap-y-2 p-0">
        {rows.map((row) => (
          <li
            key={row.status}
            className="border-border-subtle bg-surface-sunken/60 inline-flex items-center gap-2 rounded-md border px-2.5 py-1"
          >
            <span aria-hidden="true" className={`size-2 shrink-0 rounded-full ${row.dot}`} />
            <span className="text-text-muted text-sm">{row.label}</span>
            <span className={`text-sm font-semibold tabular-nums ${row.ink}`}>{row.value}</span>
          </li>
        ))}
      </ul>
      {unrecognised !== null && unrecognised > 0 ? (
        // Non-zero only when the database is ahead of this bundle. Said out loud rather than folded
        // into a bucket, because a number that silently absorbed it would be wrong and confident.
        <p className="text-text-muted m-0 mt-2.5 text-xs">
          {unrecognised} envelope{unrecognised === 1 ? ' is' : 's are'} in a status this page does
          not recognise yet, and {unrecognised === 1 ? 'is' : 'are'} counted in none of the figures
          above.
        </p>
      ) : null}
    </div>
  )
}

/* -------------------------------------------------------------------- getting started ------- */

/**
 * One setup step, and the three things it can be.
 *
 * THREE states, not two, and that is the whole redesign of this card. `done` and `todo` are claims;
 * `unknown` is the absence of one, and it is what a failed read produces. An unticked box for a
 * step whose read failed is a statement that the step is NOT done, which is a different and wrong
 * thing to tell somebody who may have finished it months ago.
 */
type StepState = 'done' | 'todo' | 'unknown'

type SetupStep = {
  readonly label: string
  readonly detail: string
  readonly state: StepState
  readonly to?: string
}

/**
 * Getting started, computed from reads rather than declared as a constant.
 *
 * It used to be a hardcoded array with step one marked done, so the card read "1 of 4 steps done"
 * for every organization forever — including one that had uploaded, placed fields, sent a hundred
 * envelopes and collected a hundred signatures. That is the same class of defect this file has
 * retracted twice before: the step that read "Arrives with the composer" after send had shipped,
 * and the tile hint that said "Signed and sealed" on a cell where nothing could be sealed. A
 * checklist that cannot be completed is not a checklist, it is an accusation.
 *
 * "Place fields" is gone. Nothing this screen can read proves whether a field was ever placed —
 * fields live under a document version and no summary counts them — and a step that can never tick
 * is the defect above with extra steps. What replaced it is a step that CAN be proved: whether this
 * person has adopted a signature, from `GET /v1/profile/signature`.
 *
 * The card removes itself once every step is done. An established tenant does not need a permanent
 * reminder that they have already started, and the rail is for context that must never outrank the
 * work beside it.
 */
function GettingStarted({
  counts,
  signature,
  profilePercent,
}: {
  readonly counts: (key: keyof EnvelopeSummaryResource) => number | null
  readonly signature: SignatureView
  /** From `GET /v1/profile`'s `completeness`. Null while it is loading, or if it could not be read. */
  readonly profilePercent: number | null
}) {
  const drafts = counts('drafts')
  const waiting = counts('waiting')
  const needsYou = counts('needs_you')
  const completed = counts('completed')

  /**
   * A sum that stays unknown if any part is.
   *
   * `?? 0` here would be the exact failure the rest of this screen refuses: it turns "I could not
   * read this" into a confident zero, and a confident zero unticks a step somebody has done.
   */
  const total = (...parts: ReadonlyArray<number | null>): number | null =>
    parts.some((part) => part === null)
      ? null
      : parts.reduce((sum, part) => (sum ?? 0) + (part ?? 0), 0)

  const anyEnvelope = total(drafts, waiting, needsYou, completed)
  const anySent = total(waiting, needsYou, completed)

  const from = (value: number | null): StepState =>
    value === null ? 'unknown' : value > 0 ? 'done' : 'todo'

  const steps: readonly SetupStep[] = [
    {
      label: 'Create your account',
      detail: 'Done — you are signed in.',
      state: 'done',
    },
    {
      // NOT "Start an envelope", and not "Upload a document" either. Both are already the
      // accessible name of a link on this page — the launcher's first card and the masthead's
      // button — and two links with one name pointing at the same place is a screen reader reading
      // the destination twice and a test that cannot say which one it meant.
      label: 'Upload and prepare a document',
      detail: 'A PDF or an Office file, with its fields placed where they belong.',
      state: from(anyEnvelope),
      to: '/app/documents',
    },
    {
      label: 'Send it for signature',
      detail: 'Add recipients, set the signing order, then send from the recipients step.',
      state: from(anySent),
      to: '/app/documents',
    },
    {
      // The wizard's row, and it disappears at 100% like every other step here. `unknown` while the
      // profile is loading rather than `todo`: a row that says "40% complete" for a moment and then
      // vanishes is a card that lied for a moment, which is the same rule the signature row states.
      label:
        profilePercent === null
          ? 'Complete your profile'
          : `Complete your profile — ${profilePercent}%`,
      detail:
        'Your name, signature, initials and phone number, saved once and offered on every document.',
      state: profilePercent === null ? 'unknown' : profilePercent >= 100 ? 'done' : 'todo',
      to: '/app/welcome',
    },
    {
      label: 'Adopt your signature',
      detail: 'Draw or type it once; every document you sign afterwards reuses it.',
      state:
        signature === 'adopted'
          ? 'done'
          : signature === 'none'
            ? 'todo'
            : // `loading` counts as unknown on purpose: a tick that appears a moment after the card
              // does is a card that lied for a moment.
              'unknown',
      to: '/app/inbox',
    },
  ]

  const done = steps.filter((step) => step.state === 'done').length
  const unknown = steps.some((step) => step.state === 'unknown')

  // Nothing left to say. The card is guidance, and guidance that has been followed is clutter.
  if (!unknown && done === steps.length) return null

  return (
    <Card
      title="Getting started"
      description={
        unknown
          ? `${done} of ${steps.length} steps done so far`
          : `${done} of ${steps.length} steps done`
      }
    >
      {/* A track and a fill rather than a `<progress>`: the element is styleable in exactly one
          browser and this has to look the same in all of them. `aria-hidden` because the card's
          own description already says "2 of 4 steps done" in words. */}
      <div aria-hidden="true" className="bg-surface-sunken mb-4 h-1.5 w-full rounded-full">
        <div
          className="bg-accent ease-standard h-1.5 rounded-full transition-[width] duration-500"
          style={{ width: `${(done / steps.length) * 100}%` }}
        />
      </div>
      <ol className="m-0 flex list-none flex-col gap-3 p-0">
        {steps.map((step, index) => (
          <Step key={step.label} step={step} number={index + 1} />
        ))}
      </ol>
    </Card>
  )
}

function Step({ step, number }: { readonly step: SetupStep; readonly number: number }) {
  const { label, detail, state, to } = step
  return (
    <li className="flex items-start gap-3">
      {/* Numbered, not an empty ring. An unfilled circle beside a label is the shape of a radio
          button, and three of them in a column read as a form somebody forgot to fill in. An
          UNKNOWN step is a dash — neither ticked nor numbered — with the reason in the twin. */}
      <span
        aria-hidden="true"
        className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full text-[0.625rem] font-semibold tabular-nums ${
          state === 'done'
            ? 'bg-success text-text-on-accent'
            : 'border-border-strong text-text-muted border border-dashed'
        }`}
      >
        {state === 'done' ? <Icon name="check" size={12} /> : state === 'unknown' ? '—' : number}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-text-primary m-0 text-sm font-medium">
          {to !== undefined && state !== 'done' ? (
            <Link to={to} className="hover:text-accent hover:underline">
              {label}
            </Link>
          ) : (
            label
          )}
          {state === 'unknown' ? (
            <span className="visually-hidden"> — not yet available</span>
          ) : null}
        </p>
        <p className="text-text-muted m-0 text-xs">
          {state === 'unknown' ? 'This could not be checked just now.' : detail}
        </p>
      </div>
    </li>
  )
}
